/**
 * IntegrationSettingsPage - Admin UI for managing API keys and testing connections
 */
import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import AdminSidebar from "../components/layout/AdminSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Mail, Phone, CreditCard, Bell, Brain, Save, Trash2,
  CheckCircle, XCircle, Loader2, Eye, EyeOff, Plug, TestTube2, Shield
} from "lucide-react";

const PROVIDER_ICONS = {
  sendgrid: <Mail className="w-5 h-5" />,
  twilio: <Phone className="w-5 h-5" />,
  stripe: <CreditCard className="w-5 h-5" />,
  vapid: <Bell className="w-5 h-5" />,
};

const PROVIDER_COLORS = {
  sendgrid: "from-blue-500/10 to-blue-600/5 border-blue-200",
  twilio: "from-red-500/10 to-red-600/5 border-red-200",
  stripe: "from-purple-500/10 to-purple-600/5 border-purple-200",
  vapid: "from-amber-500/10 to-amber-600/5 border-amber-200",
};

const SETUP_URLS = {
  sendgrid: "https://app.sendgrid.com/settings/api_keys",
  twilio: "https://console.twilio.com/",
  stripe: "https://dashboard.stripe.com/apikeys",
  vapid: null,
};

const IntegrationSettingsPage = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [editValues, setEditValues] = useState({});
  const [showValues, setShowValues] = useState({});
  const [saving, setSaving] = useState({});
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    if (token) fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/integrations/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
    } catch (err) {
      toast.error("Failed to load integration settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (key) => {
    const value = editValues[key];
    if (!value?.trim()) {
      toast.error("Value cannot be empty");
      return;
    }

    setSaving(p => ({ ...p, [key]: true }));
    try {
      await axios.post(`${API}/integrations/settings`,
        { key, value: value.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${key} saved successfully`);
      setEditValues(p => ({ ...p, [key]: "" }));
      fetchSettings();
    } catch (err) {
      toast.error(`Failed to save ${key}`);
    } finally {
      setSaving(p => ({ ...p, [key]: false }));
    }
  };

  const handleDelete = async (key) => {
    try {
      await axios.delete(`${API}/integrations/settings/${key}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${key} removed (reverted to environment variable)`);
      fetchSettings();
    } catch (err) {
      toast.error(`Failed to remove ${key}`);
    }
  };

  const handleTest = async (provider) => {
    setTesting(p => ({ ...p, [provider]: true }));
    setTestResults(p => ({ ...p, [provider]: null }));
    try {
      const res = await axios.post(`${API}/integrations/test/${provider}`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResults(p => ({ ...p, [provider]: res.data }));
      if (res.data.success) {
        toast.success(`${provider}: ${res.data.message}`);
      } else {
        toast.error(`${provider}: ${res.data.error}`);
      }
    } catch (err) {
      setTestResults(p => ({ ...p, [provider]: { success: false, error: "Test request failed" } }));
      toast.error("Connection test failed");
    } finally {
      setTesting(p => ({ ...p, [provider]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <AdminSidebar />
        <main className="flex-1 p-6 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      <main className="flex-1 p-6 max-w-5xl" data-testid="integration-settings-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold font-serif flex items-center gap-2">
            <Plug className="w-6 h-6" />
            Integration Settings
          </h1>
          <p className="text-muted-foreground">
            Configure API keys for email, SMS, payments, and push notifications.
            Keys are stored securely and take effect immediately.
          </p>
        </div>

        <div className="space-y-6">
          {Object.entries(settings).map(([providerId, provider]) => (
            <Card
              key={providerId}
              className={`bg-gradient-to-r ${PROVIDER_COLORS[providerId] || "border-gray-200"}`}
              data-testid={`provider-card-${providerId}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-3">
                    {PROVIDER_ICONS[providerId]}
                    {provider.label}
                    {provider.all_configured ? (
                      <Badge className="bg-green-100 text-green-700 border-green-200" data-testid={`status-${providerId}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Configured
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-300" data-testid={`status-${providerId}`}>
                        Not Configured
                      </Badge>
                    )}
                  </CardTitle>
                  <div className="flex gap-2">
                    {SETUP_URLS[providerId] && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(SETUP_URLS[providerId], "_blank")}
                        data-testid={`setup-link-${providerId}`}
                      >
                        Get Keys
                      </Button>
                    )}
                    {["sendgrid", "twilio", "stripe"].includes(providerId) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTest(providerId)}
                        disabled={testing[providerId] || !provider.all_configured}
                        data-testid={`test-btn-${providerId}`}
                      >
                        {testing[providerId] ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <TestTube2 className="w-3 h-3 mr-1" />
                        )}
                        Test Connection
                      </Button>
                    )}
                  </div>
                </div>
                {testResults[providerId] && (
                  <div className={`mt-2 p-2 rounded text-sm ${testResults[providerId].success ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
                    data-testid={`test-result-${providerId}`}
                  >
                    {testResults[providerId].success ? (
                      <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" /> {testResults[providerId].message}</span>
                    ) : (
                      <span className="flex items-center gap-1"><XCircle className="w-4 h-4" /> {testResults[providerId].error}</span>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(provider.keys).map(([keyName, keyInfo]) => (
                  <div key={keyName} className="space-y-2" data-testid={`key-field-${keyName}`}>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Shield className="w-3 h-3 text-muted-foreground" />
                      {keyName}
                    </Label>
                    <p className="text-xs text-muted-foreground -mt-1">{keyInfo.description}</p>
                    <div className="flex gap-2">
                      {keyInfo.configured ? (
                        <>
                          <div className="flex-1 relative">
                            <Input
                              value={editValues[keyName] || ""}
                              onChange={e => setEditValues(p => ({ ...p, [keyName]: e.target.value }))}
                              placeholder={showValues[keyName] ? keyInfo.masked_value : "Enter new value to update..."}
                              type={showValues[keyName] ? "text" : "password"}
                              className="pr-10"
                              data-testid={`input-${keyName}`}
                            />
                            <button
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              onClick={() => setShowValues(p => ({ ...p, [keyName]: !p[keyName] }))}
                              type="button"
                            >
                              {showValues[keyName] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleSave(keyName)}
                            disabled={!editValues[keyName]?.trim() || saving[keyName]}
                            data-testid={`save-btn-${keyName}`}
                          >
                            {saving[keyName] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(keyName)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`delete-btn-${keyName}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Badge variant="outline" className="self-center text-xs whitespace-nowrap">
                            {keyInfo.source === "database" ? "DB" : "ENV"}
                          </Badge>
                        </>
                      ) : (
                        <>
                          <Input
                            value={editValues[keyName] || ""}
                            onChange={e => setEditValues(p => ({ ...p, [keyName]: e.target.value }))}
                            placeholder={keyInfo.description}
                            type="text"
                            className="flex-1"
                            data-testid={`input-${keyName}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => handleSave(keyName)}
                            disabled={!editValues[keyName]?.trim() || saving[keyName]}
                            data-testid={`save-btn-${keyName}`}
                          >
                            {saving[keyName] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          </Button>
                        </>
                      )}
                    </div>
                    {keyInfo.configured && keyInfo.source && (
                      <p className="text-xs text-muted-foreground">
                        Current: {keyInfo.masked_value}
                        {keyInfo.updated_at && ` (updated ${new Date(keyInfo.updated_at).toLocaleDateString()})`}
                      </p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

export default IntegrationSettingsPage;
